import type {
  IPublicClientApplication,
  AccountInfo,
} from '@azure/msal-browser';
import { API_BASE_URL } from '../constants';
import type { IDataItem } from '../models/IDataItem';
import { scopes } from '../authentication/PublicClientApplication';
import type { IPersonalListItem } from '../models/IPersonalListItem';
import type { IStammdatenItem } from '../models/IStammdatenItem';
import type { IMonthItem } from '../models/IMonthItem';
import type { IMonthSingleItem } from '../models/IMonthSingleItem';
import type { IProjectItem } from '../models/IProjectItem';
import type { IUserPermissionItem } from '../models/IUserPermissionItem';
export class DataService {
  private instance: IPublicClientApplication;
  private account: AccountInfo;

  constructor(instance: IPublicClientApplication, account: AccountInfo) {
    this.instance = instance;
    this.account = account;
  }

  async fetchWithAuth(url: string, options: RequestInit = {}) {
    const response = await this.instance.acquireTokenSilent({
      scopes,
      account: this.account,
    });
    const headers = {
      ...options.headers,
      Authorization: `Bearer ${response.accessToken}`,
    };
    return fetch(url, { ...options, headers });
  }
  async getAppRoles(): Promise<IDataItem[]> {
    const url = `${API_BASE_URL}/fhelix/general/permission/rollen?appId=1`;
    const res = await this.fetchWithAuth(url, {
      method: 'GET',
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error('API Error:', res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`
      );
    }
    return res.json() as Promise<IDataItem[]>;
  }

  async deleteUserRole(itemId: number): Promise<boolean> {
    const url = `${API_BASE_URL}/fhelix/general/permission/delete?itemId=${itemId}`;
    const res = await this.fetchWithAuth(url, {
      method: 'DELETE',
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error('API Error:', res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`
      );
    }
    return true;
  }

  async addUserRole(item: IUserPermissionItem): Promise<boolean | string> {
    const url = `${API_BASE_URL}/fhelix/general/permission/add`;
    const res = await this.fetchWithAuth(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(item),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error('API Error:', res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`
      );
    } else {
      return res.json() as Promise<boolean | string>;
    }
  }

  async getRolesAppPermissions(
    rollenId: string
  ): Promise<IUserPermissionItem[]> {
    const url = `${API_BASE_URL}/fhelix/general/permission/rolesapppermissions?appId=1&rollenId=${rollenId}`;
    const res = await this.fetchWithAuth(url, {
      method: 'GET',
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error('API Error:', res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`
      );
    }
    return res.json() as Promise<IUserPermissionItem[]>;
  }

  async getUserAppPermissions(
    userUpn: string,
    appId: string
  ): Promise<IUserPermissionItem[]> {
    const url = `${API_BASE_URL}/fhelix/general/permission/userpermissions?userUpn=${userUpn}&appId=${appId}`;
    const res = await this.fetchWithAuth(url, {
      method: 'GET',
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error('API Error:', res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`
      );
    }
    return res.json() as Promise<IUserPermissionItem[]>;
  }

  async getMitarbeiterAuswahlliste(
    begin: Date,
    end: Date
  ): Promise<IDataItem[]> {
    const url = `${API_BASE_URL}/fhelix/personal/mitarbeiterauwahlliste?von=${begin.toDateString()}&bis=${end.toDateString()}`;
    const res = await this.fetchWithAuth(url, {
      method: 'GET',
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error('API Error:', res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`
      );
    }
    return res.json() as Promise<IDataItem[]>;
  }

  async getMitarbeiterSearchByUpn(upn: string): Promise<IDataItem[]> {
    const url = `${API_BASE_URL}/fhelix/personal/mitarbeitersearchbyupn?search=${upn}`;
    const res = await this.fetchWithAuth(url, {
      method: 'GET',
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error('API Error:', res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`
      );
    } else {
      return res.json() as Promise<IDataItem[]>;
    }
  }

  async getBerufsartliste(art: string): Promise<IDataItem[]> {
    const url = `${API_BASE_URL}/fhelix/personal/berufsartliste?art=${art}`;
    const res = await this.fetchWithAuth(url, {
      method: 'GET',
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error('API Error:', res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`
      );
    }
    return res.json() as Promise<IDataItem[]>;
  }

  async getPersonalliste(
    art: string,
    taetigkeiten: string[],
    inaktiv: boolean,
    search: string
  ): Promise<IPersonalListItem[]> {
    const taetigkeitenParam =
      taetigkeiten !== null ? taetigkeiten.join(',') : '';
    const url = `${API_BASE_URL}/fhelix/personal/personalliste?berufsart=${art}&taetigkeiten=${taetigkeitenParam}&inaktiv=${inaktiv}&search=${search}`;
    const res = await this.fetchWithAuth(url, {
      method: 'GET',
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error('API Error:', res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`
      );
    }
    return res.json() as Promise<IPersonalListItem[]>;
  }

  async getPersonalStammdaten(guid: string): Promise<IStammdatenItem> {
    const url = `${API_BASE_URL}/fhelix/personal/stammdaten?guid=${guid}`;
    const res = await this.fetchWithAuth(url, {
      method: 'GET',
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error('API Error:', res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`
      );
    }
    return res.json() as Promise<IStammdatenItem>;
  }

  async getTimetrackingPersonalMonth(
    guid: string,
    jahre: string[]
  ): Promise<IMonthItem[]> {
    const jahreParam = jahre !== null ? jahre.join(',') : '';
    const url = `${API_BASE_URL}/fhelix/personal/monthlist?guid=${guid}&jahre=${jahreParam}`;
    const res = await this.fetchWithAuth(url, {
      method: 'GET',
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error('API Error:', res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`
      );
    }
    return res.json() as Promise<IMonthItem[]>;
  }

  async getMonthCompletion(
    year: string,
    month: string,
    guid: string
  ): Promise<IMonthSingleItem[]> {
    const url = `${API_BASE_URL}/staff/timetracking/monthcompletion?year=${year}&month=${month}&guid=${guid}`;
    const res = await this.fetchWithAuth(url, {
      method: 'GET',
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error('API Error:', res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`
      );
    }
    return res.json() as Promise<IMonthSingleItem[]>;
  }

  async getMonthOverview(date: Date, guid: string): Promise<IMonthItem> {
    const url = `${API_BASE_URL}/fhelix/personal/monthoverview?date=${date.toDateString()}&guid=${guid}`;
    const res = await this.fetchWithAuth(url, {
      method: 'GET',
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error('API Error:', res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`
      );
    }
    return res.json() as Promise<IMonthItem>;
  }

  async getProjectsHoursMonth(
    date: Date,
    guid: string
  ): Promise<IProjectItem[]> {
    const url = `${API_BASE_URL}/fhelix/personal/myprojectshoursmonth?date=${date.toDateString()}&guid=${guid}`;
    const res = await this.fetchWithAuth(url, {
      method: 'GET',
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error('API Error:', res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`
      );
    }
    return res.json() as Promise<IProjectItem[]>;
  }

  async getMonthCompletionPdf(
    year: string,
    month: string,
    guid: string
  ): Promise<Blob> {
    const url = `${API_BASE_URL}/fhelix/personal/monthcompletionpdf?year=${year}&month=${month}&guid=${guid}`;
    const res = await this.fetchWithAuth(url, {
      method: 'GET',
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error('API Error:', res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`
      );
    }
    return (await res).blob();
  }

  async downloadDocument(
    mitarbeiterGuid: string,
    documentid: string
  ): Promise<Blob> {
    const url = `${API_BASE_URL}/staff/timetracking/downloaddocument?mitarbeiterguid=${mitarbeiterGuid}&documentid=${documentid}`;
    const res = await this.fetchWithAuth(url, {
      method: 'GET',
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error('API Error:', res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`
      );
    }
    return (await res).blob();
  }

  async addMonthPersonal(item: IMonthItem): Promise<boolean | string> {
    const url = `${API_BASE_URL}/fhelix/personal/addmonthpersonal`;
    const res = await this.fetchWithAuth(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(item),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error('API Error:', res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`
      );
    }
    return res.json() as Promise<boolean | string>;
  }

  async deleteMonthItem(itemId: number): Promise<boolean> {
    const url = `${API_BASE_URL}/fhelix/personal/deletemonthitem?itemId=${itemId}`;
    const res = await this.fetchWithAuth(url, {
      method: 'DELETE',
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error('API Error:', res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`
      );
    }
    return true;
  }

  async updateMonthItem(item: IMonthItem): Promise<boolean | string> {
    const url = `${API_BASE_URL}/fhelix/personal/updatemonthitem`;
    const res = await this.fetchWithAuth(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(item),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error('API Error:', res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`
      );
    }
    return res.json() as Promise<boolean | string>;
  }

  async getTimetrackingMonths(
    begin: Date,
    end: Date,
    mitarbeiterGuid: string,
    status: string
  ): Promise<IMonthItem[]> {
    const url = `${API_BASE_URL}/fhelix/personal/timetrackingmonths?von=${begin.toDateString()}&bis=${end.toDateString()}&mitarbeiterGuid=${mitarbeiterGuid}&status=${status}`;
    const res = await this.fetchWithAuth(url, {
      method: 'GET',
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error('API Error:', res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`
      );
    }
    return res.json() as Promise<IMonthItem[]>;
  }

  async getTimetrackingMonthsExcel(
    begin: Date,
    end: Date,
    mitarbeiterGuid: string,
    status: string
  ): Promise<string> {
    const url = `${API_BASE_URL}/fhelix/personal/timetrackingmonthsexcel?von=${begin.toDateString()}&bis=${end.toDateString()}&mitarbeiterGuid=${mitarbeiterGuid}&status=${status}`;
    const res = await this.fetchWithAuth(url, {
      method: 'GET',
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error('API Error:', res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`
      );
    }
    return (await res).text();
  }

  async getAllMonthCompletionPdf(
    begin: Date,
    end: Date,
    mitarbeiterGuid: string
  ): Promise<Blob> {
    const url = `${API_BASE_URL}/fhelix/personal/allmonthcompletionpdf?von=${begin.toDateString()}&bis=${end.toDateString()}&mitarbeiterGuid=${mitarbeiterGuid}`;
    const res = await this.fetchWithAuth(url, {
      method: 'GET',
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error('API Error:', res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`
      );
    }
    return (await res).blob();
  }

  async getAllMonthCompletionExcel(
    begin: Date,
    end: Date,
    mitarbeiterGuid: string
  ): Promise<string> {
    const url = `${API_BASE_URL}/fhelix/personal/allmonthcompletionexcel?von=${begin.toDateString()}&bis=${end.toDateString()}&mitarbeiterGuid=${mitarbeiterGuid}`;
    const res = await this.fetchWithAuth(url, {
      method: 'GET',
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error('API Error:', res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`
      );
    }
    return (await res).text();
  }
}
